import { AuthResponseError } from './auth-outcome'

/** Bounded authentication diagnostics. Never retain or export raw error payloads. */
export type AuthMethod = 'email' | 'oauth_google' | 'oauth_github' | 'unknown'
export type AuthStage = 'oauth_start' | 'oauth_callback' | 'oauth_callback_validation' | 'password_login' | 'signup' | 'verification_submit' | 'verification_resend' | 'session_check' | 'password_recovery_request' | 'password_recovery_resend' | 'password_reset'
export type AuthCompletionOutcome = 'success' | 'verification_required' | 'cancelled' | 'restart_required'
export type AuthFailureReason = 'invalid_response' | 'state_mismatch' | 'missing_callback' | 'provider_error' | 'provider_cancelled' | 'verification_rejected' | 'session_missing'
export type AuthFailureCategory = 'user_input' | 'auth_rejected' | 'service' | 'network' | 'rate_limited' | 'invalid_response' | 'callback_validation' | 'cancelled' | 'unknown'

export interface AuthOperationInput {
  method: AuthMethod
  stage: AuthStage
}

export interface AuthOperationRecorder {
  complete: (outcome: AuthCompletionOutcome) => void
  fail: (error?: unknown, reason?: AuthFailureReason) => void
}

export interface AuthOperationEvent {
  method: AuthMethod
  stage: AuthStage | 'unknown'
  outcome: AuthCompletionOutcome | 'failure'
  failure_category: AuthFailureCategory | null
  failure_reason?: AuthFailureReason
  error_code: string | null
  status: number | null
  duration_ms: number
  alert_worthy: boolean
  telemetry_version: 1
}

const METHODS = new Set(['email', 'oauth_google', 'oauth_github', 'unknown'])
const STAGES = new Set(['oauth_start', 'oauth_callback', 'oauth_callback_validation', 'password_login', 'signup', 'verification_submit', 'verification_resend', 'session_check', 'password_recovery_request', 'password_recovery_resend', 'password_reset'])
const OUTCOMES = new Set(['success', 'verification_required', 'cancelled', 'restart_required'])
const REASONS = new Set(['invalid_response', 'state_mismatch', 'missing_callback', 'provider_error', 'provider_cancelled', 'verification_rejected', 'session_missing'])
const ERROR_CODES = new Set([
  'CANCELED',
  'CANCELLED',
  'UNKNOWN',
  'INVALID_ARGUMENT',
  'DEADLINE_EXCEEDED',
  'NOT_FOUND',
  'ALREADY_EXISTS',
  'PERMISSION_DENIED',
  'RESOURCE_EXHAUSTED',
  'FAILED_PRECONDITION',
  'ABORTED',
  'OUT_OF_RANGE',
  'UNIMPLEMENTED',
  'INTERNAL',
  'UNAVAILABLE',
  'DATA_LOSS',
  'UNAUTHENTICATED',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'BAD_REQUEST',
  'CONFLICT',
  'TOO_MANY_REQUESTS',
  'INTERNAL_SERVER_ERROR',
  'BAD_GATEWAY',
  'SERVICE_UNAVAILABLE',
  'GATEWAY_TIMEOUT',
])
const SERVICE_CODES = new Set(['INTERNAL', 'INTERNAL_SERVER_ERROR', 'UNAVAILABLE', 'SERVICE_UNAVAILABLE', 'UNIMPLEMENTED', 'DATA_LOSS', 'DEADLINE_EXCEEDED', 'BAD_GATEWAY', 'GATEWAY_TIMEOUT'])
const REJECTION_CODES = new Set(['UNAUTHENTICATED', 'UNAUTHORIZED', 'FORBIDDEN', 'PERMISSION_DENIED', 'FAILED_PRECONDITION'])
const NETWORK_CODES = new Set(['ERR_NETWORK', 'ECONNRESET', 'ECONNREFUSED', 'ENOTFOUND', 'ETIMEDOUT'])

function property(value: unknown, key: string): unknown {
  try {
    return value !== null && typeof value === 'object'
      ? (value as Record<string, unknown>)[key]
      : undefined
  }
  catch {
    return undefined
  }
}

function statusNumber(value: unknown): number | undefined {
  if (typeof value === 'string' && /^[1-5]\d{2}$/.test(value))
    return Number(value)
  if (typeof value === 'number' && Number.isInteger(value) && value >= 100 && value <= 599)
    return value
  return undefined
}

function knownCode(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length > 40)
    return undefined
  const code = value.toUpperCase()
  return ERROR_CODES.has(code) ? code : undefined
}

function failureSignals(error: unknown) {
  let status: number | undefined
  let code: string | undefined
  let cancelled = false
  let network = false
  let verificationRejected = false
  let invalidResponse = false
  const queue = [error]
  const seen = new Set<object>()
  // ApiError.error, Result.data and Error.cause can form nested/cyclic graphs.
  // Visit only explicit envelope fields, with a strict node/array bound.
  for (let index = 0; index < queue.length && index < 24; index++) {
    const value = queue[index]
    if (value === null || typeof value !== 'object' || seen.has(value))
      continue
    seen.add(value)
    invalidResponse ||= value instanceof AuthResponseError
    const nextStatus = statusNumber(property(value, 'status'))
    if (nextStatus !== undefined && (status === undefined || (status < 400 && nextStatus >= 400)))
      status = nextStatus
    code ??= knownCode(property(value, 'code')) ?? knownCode(property(value, 'id'))
    const name = property(value, 'name')
    cancelled ||= name === 'AbortError'
    verificationRejected ||= property(value, 'verified') === false
    const message = property(value, 'message')
    const rawCode = property(value, 'code')
    network ||= name === 'NetworkError' || name === 'TimeoutError'
      || (typeof rawCode === 'string' && NETWORK_CODES.has(rawCode))
      || (name === 'TypeError' && typeof message === 'string' && /^(?:Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?)$/i.test(message))
    for (const key of ['error', 'data', 'cause', 'response']) {
      if (queue.length < 24)
        queue.push(property(value, key))
    }
    const errors = property(value, 'errors')
    if (Array.isArray(errors)) {
      for (let i = 0; i < Math.min(errors.length, 4) && queue.length < 24; i++)
        queue.push(property(errors, String(i)))
    }
  }
  return { status, code, cancelled, network, verificationRejected, invalidResponse }
}

function classifyFailure(stage: AuthOperationEvent['stage'], error: unknown, reason?: AuthFailureReason) {
  const signals = failureSignals(error)
  const { status, code } = signals
  let category: AuthFailureCategory = 'unknown'
  if (signals.cancelled || reason === 'provider_cancelled' || code === 'CANCELED' || code === 'CANCELLED')
    category = 'cancelled'
  else if (status === 429 || code === 'RESOURCE_EXHAUSTED' || code === 'TOO_MANY_REQUESTS')
    category = 'rate_limited'
  else if ((status !== undefined && status >= 500) || (code !== undefined && SERVICE_CODES.has(code)))
    category = 'service'
  else if (signals.network)
    category = 'network'
  else if (reason === 'state_mismatch' || reason === 'missing_callback')
    category = 'callback_validation'
  else if (reason === 'invalid_response' || signals.invalidResponse)
    category = 'invalid_response'
  else if (stage === 'password_login' && (status === 401 || code === 'UNAUTHORIZED' || code === 'UNAUTHENTICATED'))
    category = 'user_input'
  else if (stage === 'signup' && (status === 409 || code === 'ALREADY_EXISTS' || code === 'CONFLICT'))
    category = 'user_input'
  else if (['password_recovery_request', 'password_recovery_resend'].includes(stage) && status === 403)
    category = 'user_input'
  else if (status === 401 || status === 403 || (code !== undefined && REJECTION_CODES.has(code)))
    category = 'auth_rejected'
  else if (['password_login', 'signup', 'verification_submit', 'verification_resend', 'password_recovery_request', 'password_recovery_resend', 'password_reset'].includes(stage) && (status === 400 || code === 'INVALID_ARGUMENT' || code === 'BAD_REQUEST'))
    category = 'user_input'
  else if (['verification_submit', 'verification_resend'].includes(stage) && (signals.verificationRejected || reason === 'verification_rejected'))
    category = 'user_input'
  else if (reason === 'provider_error' || reason === 'session_missing' || (status !== undefined && status >= 400))
    category = 'auth_rejected'
  return {
    outcome: category === 'cancelled' ? 'cancelled' as const : 'failure' as const,
    failure_category: category,
    ...(reason ? { failure_reason: reason } : {}),
    error_code: code ?? null,
    status: status ?? null,
    alert_worthy: category !== 'user_input' && category !== 'cancelled',
  }
}

function clock(now: () => number): number {
  try {
    const value = now()
    return Number.isFinite(value) && value >= 0 ? value : 0
  }
  catch {
    return 0
  }
}

/** One terminal event per operation, even when catch/finally both report it. */
export function startAuthOperation(
  input: AuthOperationInput,
  capture: (event: AuthOperationEvent) => void,
  now: () => number = () => performance.now(),
): AuthOperationRecorder {
  const rawMethod = property(input, 'method')
  const rawStage = property(input, 'stage')
  const method = typeof rawMethod === 'string' && METHODS.has(rawMethod) ? rawMethod as AuthMethod : 'unknown'
  const stage = typeof rawStage === 'string' && STAGES.has(rawStage) ? rawStage as AuthStage : 'unknown'
  const started = clock(now)
  let completed = false
  function finish(fields: Omit<AuthOperationEvent, 'method' | 'stage' | 'duration_ms' | 'telemetry_version'>) {
    if (completed)
      return
    completed = true
    try {
      capture({ method, stage, ...fields, duration_ms: Math.max(0, Math.round(clock(now) - started)), telemetry_version: 1 })
    }
    catch {
      // Analytics, consent storage and SDK failures must never affect auth.
    }
  }
  function fail(error?: unknown, rawReason?: AuthFailureReason) {
    if (completed)
      return
    const reason = typeof rawReason === 'string' && REASONS.has(rawReason) ? rawReason : undefined
    try {
      finish(classifyFailure(stage, error, reason))
    }
    catch {
      finish({ outcome: 'failure', failure_category: 'unknown', error_code: null, status: null, alert_worthy: true })
    }
  }
  return {
    complete(outcome) {
      if (typeof outcome !== 'string' || !OUTCOMES.has(outcome)) {
        fail(undefined, 'invalid_response')
        return
      }
      finish({ outcome, failure_category: outcome === 'cancelled' ? 'cancelled' : null, error_code: null, status: null, alert_worthy: false })
    },
    fail,
  }
}
